import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, CheckCircle2, Save, Trash2, FolderOpen, Download, Upload } from "lucide-react";
import {
  CONTACT_SYSTEM_FIELDS,
  autoMapFields,
  validateMapping,
  FieldMapping,
} from "@/utils/columnMapper";
import {
  saveMappingTemplate,
  getMappingTemplates,
  deleteMappingTemplate,
  MappingTemplate,
  exportMappingTemplate,
  exportAllMappingTemplates,
  importMappingTemplate,
} from "@/utils/mappingTemplates";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";

interface ColumnMapperDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  csvHeaders: string[];
  onConfirm: (mapping: FieldMapping) => void;
  type: 'contacts' | 'entities';
}

export const ColumnMapperDialog = ({
  open,
  onOpenChange,
  csvHeaders,
  onConfirm,
  type,
}: ColumnMapperDialogProps) => {
  const [mapping, setMapping] = useState<FieldMapping>({});
  const [errors, setErrors] = useState<string[]>([]);
  const [savedTemplates, setSavedTemplates] = useState<MappingTemplate[]>([]);
  const [templateName, setTemplateName] = useState("");
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Apenas contacts é suportado por enquanto
  const systemFields = type === 'contacts' ? CONTACT_SYSTEM_FIELDS : [];

  // Auto-mapear quando o dialog abrir
  useEffect(() => {
    if (open && csvHeaders.length > 0) {
      const autoMapping = autoMapFields(csvHeaders, systemFields);
      setMapping(autoMapping);
      
      // Carregar templates salvos
      const templates = getMappingTemplates(type);
      setSavedTemplates(templates);
    }
  }, [open, csvHeaders, type]);

  const handleMappingChange = (systemField: string, csvColumn: string | null) => {
    setMapping(prev => ({
      ...prev,
      [systemField]: csvColumn === "none" ? null : csvColumn
    }));
  };

  const handleConfirm = () => {
    const validation = validateMapping(mapping, systemFields);
    
    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }

    setErrors([]);
    onConfirm(mapping);
  };

  const handleSaveTemplate = () => {
    if (!templateName.trim()) {
      toast.error("Digite um nome para o template");
      return;
    }

    saveMappingTemplate(templateName, mapping, type);
    
    // Atualizar lista de templates
    const templates = getMappingTemplates(type);
    setSavedTemplates(templates);
    
    setTemplateName("");
    setShowSaveTemplate(false);
    
    toast.success("Template salvo com sucesso!");
  };

  const handleLoadTemplate = (template: MappingTemplate) => {
    setMapping(template.mapping);
    toast.success(`Template "${template.name}" carregado`);
  };

  const handleDeleteTemplate = (id: string, name: string) => {
    deleteMappingTemplate(id);
    
    // Atualizar lista de templates
    const templates = getMappingTemplates(type);
    setSavedTemplates(templates);
    
    toast.success(`Template "${name}" excluído`);
  };

  const handleExportTemplate = (template: MappingTemplate) => {
    try {
      exportMappingTemplate(template);
      toast.success(`Template "${template.name}" exportado!`);
    } catch (error) {
      toast.error("Erro ao exportar template");
    }
  };

  const handleExportAll = () => {
    try {
      exportAllMappingTemplates(type);
      toast.success("Todos os templates exportados!");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Erro ao exportar templates");
    }
  };

  const handleImportTemplate = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);

    try {
      const result = await importMappingTemplate(file, type);

      if (result.success) {
        const templates = getMappingTemplates(type);
        setSavedTemplates(templates);

        if (result.template) {
          setMapping(result.template.mapping);
        }

        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error("Erro ao importar template");
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const getMappingStatus = (field: { key: string; required: boolean }) => {
    const mapped = mapping[field.key];
    
    if (mapped) {
      return <Badge variant="default" className="bg-success text-success-foreground">✓ Mapeado</Badge>;
    }
    
    if (field.required) {
      return <Badge variant="destructive">✗ Requerido</Badge>;
    }
    
    return <Badge variant="secondary">⚠ Opcional</Badge>;
  };

  const mappedCount = Object.values(mapping).filter(v => v !== null).length;
  const totalFields = systemFields.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Mapear Colunas do CSV</DialogTitle>
          <DialogDescription>
            Relacione os campos do sistema com as colunas do seu CSV. Os campos marcados com asterisco (*) são obrigatórios.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Templates Salvos e Estatísticas */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 p-3 rounded-lg bg-muted flex-1">
              <CheckCircle2 className="h-4 w-4 text-success" />
              <span className="text-sm text-muted-foreground">
                {mappedCount} de {totalFields} campos mapeados ({Math.round((mappedCount / totalFields) * 100)}%)
              </span>
            </div>
            
            {/* Botões de ação */}
            <div className="flex items-center gap-2">
              {/* Importar Template */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleImportClick}
                disabled={isImporting}
              >
                <Upload className="h-4 w-4 mr-2" />
                {isImporting ? "Importando..." : "Importar"}
              </Button>

              {/* Input oculto para upload */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleImportTemplate}
                className="hidden"
              />

              {/* Exportar Todos */}
              {savedTemplates.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportAll}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Exportar Todos
                </Button>
              )}

              {/* Carregar Template */}
              {savedTemplates.length > 0 && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm">
                      <FolderOpen className="h-4 w-4 mr-2" />
                      Carregar
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80" align="end">
                    <div className="space-y-3">
                      <h4 className="font-medium text-sm">Templates Salvos</h4>
                      <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {savedTemplates.map((template) => (
                          <div
                            key={template.id}
                            className="flex items-center justify-between p-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{template.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(template.createdAt).toLocaleDateString('pt-BR')}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 ml-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleLoadTemplate(template)}
                                title="Usar este template"
                              >
                                Usar
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleExportTemplate(template)}
                                title="Exportar este template"
                              >
                                <Download className="h-3 w-3 text-primary" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteTemplate(template.id, template.name)}
                                title="Excluir este template"
                              >
                                <Trash2 className="h-3 w-3 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>

          {/* Erros de validação */}
          {errors.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <ul className="list-disc list-inside">
                  {errors.map((error, idx) => (
                    <li key={idx}>{error}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Tabela de mapeamento */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[35%]">Campo do Sistema</TableHead>
                  <TableHead className="w-[45%]">Coluna do CSV</TableHead>
                  <TableHead className="w-[20%] text-right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {systemFields.map((field) => (
                  <TableRow key={field.key}>
                    <TableCell className="font-medium">
                      {field.label}
                      {field.required && <span className="text-destructive ml-1">*</span>}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={mapping[field.key] || "none"}
                        onValueChange={(value) => handleMappingChange(field.key, value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="-- Não mapear --" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- Não mapear --</SelectItem>
                          {csvHeaders.map((header) => (
                            <SelectItem key={header} value={header}>
                              {header}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      {getMappingStatus(field)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Informações adicionais */}
          <div className="text-sm text-muted-foreground space-y-1">
            <p>ℹ️ Colunas não mapeadas serão ignoradas na importação</p>
            <p>ℹ️ O mapeamento automático foi aplicado com base nos nomes das colunas</p>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <div className="flex-1">
            {showSaveTemplate ? (
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Nome do template..."
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveTemplate();
                    } else if (e.key === 'Escape') {
                      setShowSaveTemplate(false);
                      setTemplateName("");
                    }
                  }}
                  autoFocus
                  className="flex-1"
                />
                <Button size="sm" onClick={handleSaveTemplate}>
                  Salvar
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setShowSaveTemplate(false);
                    setTemplateName("");
                  }}
                >
                  Cancelar
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSaveTemplate(true)}
              >
                <Save className="h-4 w-4 mr-2" />
                Salvar Template
              </Button>
            )}
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={handleConfirm}>
              Confirmar Mapeamento
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
